import { CalendarDays, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Task, TaskStatus } from "@/api/queries";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDuration } from "@/lib/format";
import { cn } from "@/lib/utils";

import { formatDueDate, isOverdue, taskStatuses } from "./lib";

/** Due date; overdue dates are marked with text, not only color. */
export function DueDate({ task, today }: { task: Task; today: string }) {
  const { t, i18n } = useTranslation();
  if (!task.due_date) return <span className="text-muted">—</span>;
  const overdue = isOverdue(task, today);
  return (
    <span
      className={cn("inline-flex items-center gap-1 whitespace-nowrap", overdue && "font-medium text-danger")}
    >
      <CalendarDays className="size-3.5" aria-hidden="true" />
      {formatDueDate(task.due_date, i18n.resolvedLanguage ?? "ru")}
      {overdue && <span className="sr-only">{`(${t("tasks.overdue")})`}</span>}
    </span>
  );
}

/** "1 ч 20 мин / 2 ч": time spent and the estimate. */
export function TaskTime({ task }: { task: Task }) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  if (!task.tracked_seconds && task.estimate_minutes === undefined)
    return <span className="text-muted">—</span>;
  return (
    <span className="whitespace-nowrap tabular-nums">
      {formatDuration(task.tracked_seconds, lang)}
      {task.estimate_minutes !== undefined && (
        <span className="text-muted">{` / ${formatDuration(task.estimate_minutes * 60, lang)}`}</span>
      )}
    </span>
  );
}

type TaskCardProps = {
  task: Task;
  assigneeName?: string;
  today: string;
  canMove: boolean;
  dragging: boolean;
  onOpen: () => void;
  onMove: (status: TaskStatus) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
};

export function TaskCard({
  task,
  assigneeName,
  today,
  canMove,
  dragging,
  onOpen,
  onMove,
  onDragStart,
  onDragEnd,
}: TaskCardProps) {
  const { t } = useTranslation();
  const overdue = isOverdue(task, today);
  return (
    <li
      draggable={canMove}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", task.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      data-task-id={task.id}
      className={cn(
        "group flex flex-col gap-2 rounded-lg border bg-surface p-3 shadow-card",
        canMove && "cursor-grab active:cursor-grabbing",
        dragging && "opacity-50",
        overdue && "border-danger/40",
      )}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onOpen}
          aria-label={t("tasks.openTask", { name: task.title })}
          className={cn(
            "min-w-0 flex-1 cursor-pointer text-left font-medium break-words hover:text-primary",
            task.status === "done" && "text-muted line-through",
          )}
        >
          {task.title}
        </button>
        {canMove && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="-mt-1 -mr-1 size-7"
                aria-label={`${t("tasks.move")}: ${task.title}`}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>{t("tasks.move")}</DropdownMenuLabel>
              {taskStatuses
                .filter((s) => s !== task.status)
                .map((s) => (
                  <DropdownMenuItem key={s} onSelect={() => onMove(s)}>
                    {t("tasks.moveTo", { status: t(`tasks.status.${s}`) })}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted">
        {assigneeName ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <UserAvatar name={assigneeName} className="size-5 text-[9px]" />
            <span className="truncate">{assigneeName}</span>
          </span>
        ) : (
          <span>{t("tasks.unassigned")}</span>
        )}
        <span className="flex items-center gap-3">
          {task.due_date && <DueDate task={task} today={today} />}
          {(task.tracked_seconds > 0 || task.estimate_minutes !== undefined) && <TaskTime task={task} />}
        </span>
      </div>
    </li>
  );
}
